import React, { useEffect, useRef, useState } from "react";

// Custom components
import { PageTitle } from "../../../components/PageTitle";
import { BackdropLoading } from "../../../components/BackdropLoading";

// Styles
import { Container, FormButton } from "../../styles";
import { FormContainer, SectionPageTitle } from "./styles";

// API
import { WorkflowDataApi } from "../../../services/WorkflowDataApi";
import { CheckResponse } from "../../../utils/checkResponse";

// Router
import Router from "../../../router";

// Icons
import { DriveFileRenameOutline, ArrowBack } from "@mui/icons-material";
import { Box, Grid } from "@mui/material";

// Utils
import { useTranslation } from "react-i18next";
import { FormIo } from "../../../components/molecules/forms/FormIo";

// Offline functionality
import { useOffline } from "../../../Context/OfflineContext";
import { toast } from "react-toastify";
import { useNavigate, useLocation, useParams } from "react-router-dom";

export const Loto = () => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const formId = location?.state?.formId || "";
  const workflowId = location?.state?.workflowId || "";

  const props = {
    open: true,
  };
  const { t } = useTranslation();
  const {
    isFormDownloadedForOffline,
    connectionStatus,
    saveEditedOfflineFormData,
    getOfflineForm,
    getOfflineFormData,
    getEditedOfflineFormData,
    syncOfflineDataWithStorage
  } = useOffline();
  const workflowDataApi = new WorkflowDataApi();
  const { check } = new CheckResponse();

  const [form, setForm] = useState({ id: "", name: "", integrationKey: "" });
  const [formStructure, setFormStructure] = useState([]);
  const [data, setData] = useState({});
  const [isForcedOffline, setIsForcedOffline] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formInstance, setFormInstance] = useState(null);
  const [title, setTitle] = useState({name: "", prefix: ""});
  const formRef = useRef(null);

  useEffect(() => {
    const workflowId = params?.id;

    if (!workflowId) {
      setLoading(true);
      toast.error(t("lotoWorklow.messages.formIdNotFound"));
      setTimeout(() => {
        setLoading(false);
        navigateBack();
      }, 2000);
      return;
    }

    if (!connectionStatus) {
      setOfflineForm(workflowId);
    }
  }, [formId]);

  function setOfflineForm(workflowId) {
    const form = getOfflineForm(workflowId);
    const formStoredData = getOfflineFormData(workflowId);

    if (form !== null) {
      setLoading(true);
      setTitle({ name: `${t("forms.formManagement.formDetail")}`, prefix: form?.data?.code })

      setTimeout(() => {
        localStorage.setItem('previousData', JSON.stringify(form.formData?.previousData))
        localStorage.setItem('parentPreviousData', JSON.stringify(form.formData?.parentPreviousData))
        localStorage.setItem('characteristics', JSON.stringify(form.formData?.characteristics))
        localStorage.setItem('apiUrl', process.env.REACT_APP_API_URL)

        setFormStructure(form?.formData?.form?.structure || []);
        setForm(form?.formData);

        // Priorizar datos editados offline sobre los datos originales descargados
        const editedData = getEditedOfflineFormData(workflowId);
        setData(editedData?.data || formStoredData?.data || form?.formData?.data);
        setLoading(false);
      }, 2000);
    } else {
      setLoading(true);
      toast.error(t("lotoWorklow.messages.formNotFound"));
      setTimeout(() => {
        setLoading(false);
        navigateBack();
      }, 2000);
      return;
    }
  }

  // Detectar pérdida de conexión y activar modo offline automático
  useEffect(() => {
    if (
      !connectionStatus &&
      isFormDownloadedForOffline(form?.id) &&
      !isForcedOffline
    ) {
      // Se perdió la conexión y el formulario está descargado (solo ejecutar una vez)
      setIsForcedOffline(false);

      // No guardar automáticamente aquí para evitar bucles - el usuario guardará manualmente
    } else if (connectionStatus && isForcedOffline) {
      // Se recuperó la conexión
      setIsForcedOffline(false);
      toast.success(t("lotoWorklow.messages.connectionRecovered"));
    }
  }, [connectionStatus, form?.id, isFormDownloadedForOffline, isForcedOffline]);

  const renderButtons = () => {
    let buttons = [];
    let spaceGrid = 6;

    buttons = [
      {
        title: "close",
        grid: 2,
        key: "btnCancel",
        type: "cancel",
        variant: "outlined",
        isUpdateStatus: false,
      },
      {
        title: "save",
        grid: 2,
        key: "btnSave",
        variant: "contained",
        isUpdateStatus: false,
      },
    ];

    buttons.push({ title: "", grid: spaceGrid, key: "" });

    return buttons;
  };

  async function checkKeys(key) {
    if (key === "btnSave") {
      if (!formInstance.checkValidity(data, true)) {
        toast.error(t("forms.dynamicForm.messages.errorsForm"));
        return;
      }
      await handleSaveForm();
    }
    if (key === "btnCancel") {
      navigateBack();
    }
  }

  const handleSaveForm = async () => {
    const dataToSave =
      data && typeof data === "object" && data.data
        ? data.data
        : data;

    // Siempre guardar los datos, tanto online como offline
    if (!connectionStatus) {
      // Modo offline forzado - guardar en localStorage
      saveEditedOfflineFormData(form?.id, dataToSave);
      toast.success(t("lotoWorklow.messages.dataSavedOffline"));
    } else {
      await saveForm();
    }

    // Redirigir a LotoList después de guardar
    navigateBack();
  };

  async function saveForm() {

    let dataSend = {
      ...params,
      // notificationId: notificationId,
      data: data,
      users: []
    }
    setLoading(true)
    if (dataSend.data?._id) delete dataSend.data._id;
    
    let response = await workflowDataApi.saveFormData(dataSend.data);
    let result = check(response)

    if (result.status) {
      toast.success(t("lotoWorklow.messages.dataSavedSuccessfully"));
    }
    else {
      toast.error(() =>
        <div>{t('forms.dynamicForm.messages.saveForm.error')}<br />
          {result.errors}
        </div>
      );
    }
    setLoading(false)
  }

  const navigateBack = () => {
    if (workflowId) {
      navigate(Router.appWorkflow.replace(":id", workflowId));
    }
    else {
      navigate(-1);
    }
  }

  return (
    <Container open={props.open} style={{ marginBottom: 40 }}>
      <SectionPageTitle>
        <PageTitle title="" isbutton={true}
          navigate={Router.appOfflineWorkflow.replace(':id', workflowId || 1075)}
          icon={<ArrowBack />}
        />
        <PageTitle 
          title={title}
          icon={<DriveFileRenameOutline />}
          dinamicForm={true}
        />
      </SectionPageTitle>

      {!loading && (
        <>
          <FormContainer>
            <div ref={formRef}>
              <FormIo
                form={formStructure}
                data={data}
                setData={setData}
                checkKeys={checkKeys}
                readOnly={false}
                formInstance={formInstance}
                setFormInstance={setFormInstance}
              />
            </div>
            <Grid
              container
              mt={2}
              gap={1}
              sx={{ 
                justifyContent: { xs: "center", sm: "start" },
                alignItems: "center",
              }}
            >
              {renderButtons().map((row, index) => {
                return (
                  <Grid
                    item
                    xs={12}
                    sm={row.grid}
                    gap={1}
                    sx={{ 
                      justifyContent: { xs: "center", sm: "start" },
                      alignItems: "center",
                    }}
                    key={`${row.title}-${index}`}
                  >
                    {row.title && (
                      <FormButton
                        variant={row.variant}
                        type={row.type}
                        btnCancel={row?.disabled}
                        isUpdateStatus={row?.isUpdateStatus}
                        sx={{ width: "100%" }}
                        onClick={() => checkKeys(row.key)}
                        disabled={row?.disabled === false}
                      >
                        {t(`workflow.detail.${row.title}`)}
                      </FormButton>
                    )}
                  </Grid>
                );
              })}
            </Grid>

            <Box
              sx={{
                mt: 3,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 2,
              }}
            ></Box>
          </FormContainer>
        </>
      )}
      <BackdropLoading open={loading} />
    </Container>
  );
};
